import React, { FC } from 'react';
import { CSSObjectWithLabel, GroupBase, MultiValue, PlaceholderProps } from 'react-select';
import { FR_GREY_300, FR_GREY_500, FR_GREY_600, FR_PR_WHITE } from '@shared/styles/vars';
import { MultiSelectDropdownInputBase } from '@src/components/Select/MultiSelectDropdownInputBase';
import { SearchPattern } from '@src/containers/Source/interfaces/searchPattern.interface';
import { Option } from '@src/components/Select/interfaces/option.interface';

interface SearchFieldProps {
  handleSelect: (options: MultiValue<Option>) => void;
  onOptionClick: (option: SearchPattern) => void;
  onSearch: (searchValue: string) => void;
  options: SearchPattern[];
  searchValue: string;
  setIsOpen: (isOpen: boolean) => void;
  values: SearchPattern[];
}

const controlStyles = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
  ...base,
  '&:focus': { border: `0.1rem solid ${FR_GREY_300}`, boxShadow: 'none' },
  '&:hover': { border: `0.1rem solid ${FR_GREY_300}`, boxShadow: 'none' },
  'border': `0.1rem solid ${FR_GREY_300}`,
  'borderRadius': '0.6rem',
  'boxShadow': 'none',
  'color': FR_GREY_500,
  'fontSize': '1.3rem',
  'minHeight': '4rem',
  'paddingLeft': '4.8rem',
  'paddingRight': '3.6rem',
});

const valueContainerStyles = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
  ...base,
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '-ms-overflow-style': 'none',
  'display': 'block',
  'overflowX': 'auto',
  'padding': '0',
  'scrollbarWidth': 'none',
  'whiteSpace': 'nowrap',
});

const placeholderStyles = (
  base: CSSObjectWithLabel,
  props: PlaceholderProps<Option, true, GroupBase<Option>>,
): CSSObjectWithLabel => ({
  ...base,
  display: props.isFocused ? 'none' : 'inline-block',
});

const multiValueStyles = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
  ...base,
  backgroundColor: FR_PR_WHITE,
  border: `0.1rem solid ${FR_GREY_300}`,
  borderRadius: '0.6rem',
  boxShadow: 'none',
  display: 'inline-flex',
});

const multiValueLabelStyles = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
  ...base,
  color: FR_GREY_600,
  fontSize: '1.3rem',
  lineHeight: '1.6rem',
});

const multiValueRemoveStyles = (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
  ...base,
  '&:hover': {
    backgroundColor: 'transparent',
    color: FR_GREY_600,
  },
  'backgroundColor': 'transparent',
  'color': FR_GREY_600,
});

export const SearchField: FC<SearchFieldProps> = ({
  onSearch,
  setIsOpen,
  searchValue,
  options,
  handleSelect,
  onOptionClick,
  values,
}) => {
  return (
    <MultiSelectDropdownInputBase
      onFocus={() => setIsOpen(true)}
      onInputChange={(searchValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
          onSearch(searchValue);
        }
      }}
      inputValue={searchValue}
      onKeyDown={e => {
        if ([',', 'Enter'].includes(e.key)) {
          const pattern = options[0].pattern.trim();

          if (pattern.length) {
            onOptionClick(options[0]);
          }

          e.preventDefault();
        }
      }}
      isMenuOpen={false}
      styles={{
        control: controlStyles,
        multiValue: multiValueStyles,
        multiValueLabel: multiValueLabelStyles,
        multiValueRemove: multiValueRemoveStyles,
        placeholder: placeholderStyles,
        valueContainer: valueContainerStyles,
      }}
      isClearable={false}
      onChange={options => {
        const filteredOptions = options.filter(({ label }) => label.length);

        handleSelect(filteredOptions);
      }}
      placeholder="Skill, keyword, position"
      value={values.map(item => ({ label: item.pattern, value: item.id }))}
      components={{
        DropdownIndicator: () => null,
      }}
    />
  );
};
